import Logger from "../../../services/Logger";

const OpenLibraryService = {
    searchBooks: async (query, page = 1, limit = 10, sort = 'new') => {
        const sortParam = sort === 'new' ? '&sort=new' : '';
        const url = `/openlibrary/search.json?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}&fields=*,availability${sortParam}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Search failed: ${response.status}`);
        }

        const result = await response.json();
        Logger.log(result);
        return result;
    },

    getBookDetails: async (workKey) => {
        // Try proxy first since it's working via curl
        const proxyUrl = `/openlibrary${workKey}.json`;
        Logger.log("Proxy API URL:", proxyUrl);
        
        try {
            const response = await fetch(proxyUrl);

            Logger.log("Response status:", response.status);

            if (!response.ok) {
                throw new Error(`Proxy call failed: ${response.status}`);
            }

            const result = await response.json();
            Logger.log("Proxy result:", result);
            return result;
        } catch (error) {
            Logger.error("Proxy call failed:", error);
            
            // Fallback: try direct API call
            const directUrl = `https://openlibrary.org${workKey}.json`;
            Logger.log("Trying direct API URL:", directUrl);
            
            try {
                const response = await fetch(directUrl);

                if (!response.ok) {
                    throw new Error(`Direct API call failed: ${response.status}`);
                }

                const result = await response.json();
                Logger.log("Direct API result:", result);
                return result;
            } catch (directError) {
                Logger.error("Direct API call also failed:", directError);
                throw new Error(`Both proxy and direct API calls failed. Proxy error: ${error.message}, Direct error: ${directError.message}`);
            }
        }
    },

    getAuthorDetails: async (authorKey) => {
        const url = `/openlibrary${authorKey}.json`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Author details fetch failed: ${response.status}`);
        }

        const result = await response.json();
        Logger.log(result);
        return result;
    },

    getBookCover: (coverId, size = 'M') => {
        if (!coverId) return null;
        return `https://covers.openlibrary.org/b/id/${coverId}-${size}.jpg`;
    },

    getAuthorPhoto: (authorKey, size = 'M') => {
        if (!authorKey) return null;
        return `https://covers.openlibrary.org/a/olid/${authorKey.replace('/authors/', '')}-${size}.jpg`;
    },

    getSubjectBooks: async (subject, page = 1, limit = 10) => {
        const url = `/openlibrary/subjects/${encodeURIComponent(subject)}.json?page=${page}&limit=${limit}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Subject books fetch failed: ${response.status}`);
        }

        const result = await response.json();
        Logger.log(result);
        return result;
    },

    getTrendingBooks: async (limit = 10, offset = 0) => {
        // Use search API with sort=new to get recent books instead of trending
        const url = `/openlibrary/search.json?q=*&sort=new&page=${Math.floor(offset / limit) + 1}&limit=${limit}&fields=*,availability`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Recent books fetch failed: ${response.status}`);
        }

        const result = await response.json();
        Logger.log(result);
        return result;
    },

    // Get editions of a specific work
    getWorkEditions: async (workKey, limit = 5) => {
        const url = `/openlibrary${workKey}/editions.json?limit=${limit}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Work editions fetch failed: ${response.status}`);
        }

        const result = await response.json();
        Logger.log(result);
        return result;
    },

    // Get history/changes of an object
    getObjectHistory: async (objectKey) => {
        const url = `/openlibrary${objectKey}.json?m=history`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Object history fetch failed: ${response.status}`);
        }

        const result = await response.json();
        Logger.log(result);
        return result;
    },

    // Search by specific fields (more advanced search)
    searchBooksAdvanced: async (params) => {
        const { title, author, subject, publisher, language, limit = 10, page = 1, sort = 'new' } = params;
        let queryParts = [];
        
        if (title) queryParts.push(`title:${encodeURIComponent(title)}`);
        if (author) queryParts.push(`author:${encodeURIComponent(author)}`);
        if (subject) queryParts.push(`subject:${encodeURIComponent(subject)}`);
        if (publisher) queryParts.push(`publisher:${encodeURIComponent(publisher)}`);
        if (language) queryParts.push(`language:${encodeURIComponent(language)}`);
        
        const query = queryParts.join(' AND ');
        const sortParam = sort === 'new' ? '&sort=new' : '';
        const url = `/openlibrary/search.json?q=${query}&page=${page}&limit=${limit}&fields=*,availability${sortParam}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Advanced search failed: ${response.status}`);
        }

        const result = await response.json();
        Logger.log(result);
        return result;
    },

    // Get recent changes
    getRecentChanges: async (limit = 10) => {
        const url = `/openlibrary/recentchanges.json?limit=${limit}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Recent changes fetch failed: ${response.status}`);
        }

        const result = await response.json();
        Logger.log(result);
        return result;
    },

    // Check if a book has text content available
    hasTextContent: async (workKey) => {
        try {
            // First try to get editions to check for text content
            const editions = await this.getWorkEditions(workKey, 5);
            
            if (editions.entries && editions.entries.length > 0) {
                // Check if any edition has text content
                for (const edition of editions.entries) {
                    if (edition.key) {
                        try {
                            const editionDetails = await this.getBookDetails(edition.key);
                            if (editionDetails && editionDetails.text) {
                                return true;
                            }
                        } catch (editionErr) {
                            continue;
                        }
                    }
                }
            }

            // If no text content found in editions, check the work itself
            try {
                const workDetails = await this.getBookDetails(workKey);
                if (workDetails && workDetails.text) {
                    return true;
                }
            } catch (workErr) {
                // No text content available
            }

            return false;
        } catch (err) {
            Logger.warn(`Error checking text content for ${workKey}:`, err);
            return false;
        }
    },

    // Filter books to only include those with text content
    filterBooksWithTextContent: async (books) => {
        const booksWithText = [];
        
        // Process books in batches to avoid overwhelming the API
        const batchSize = 3;
        for (let i = 0; i < books.length; i += batchSize) {
            const batch = books.slice(i, i + batchSize);
            const batchPromises = batch.map(async (book) => {
                const hasText = await this.hasTextContent(book.key);
                return hasText ? book : null;
            });
            
            const batchResults = await Promise.all(batchPromises);
            booksWithText.push(...batchResults.filter(book => book !== null));
        }
        
        return booksWithText;
    },

    // Check if a book has availability data indicating readable content
    hasReadableContent: (book) => {
        if (!book.availability) return false;
        
        return (
            book.availability.status === 'full access' ||
            book.availability.available_to_browse === true ||
            book.availability.available_to_borrow === true
        );
    },

    // Filter books using availability data (faster than checking individual books)
    filterBooksByAvailability: (books) => {
        return books.filter(book => this.hasReadableContent(book));
    },

    // Project Gutenberg API methods
    searchGutenbergBooks: async (_query, _page = 1, _limit = 10) => {
        try {
            // Project Gutenberg doesn't have a direct search API, but we can use their catalog
            // For now, we'll return a placeholder that could be expanded
            
            // Since we can't directly access PG from browser due to CORS,
            // we'll return a mock response for demonstration
            return {
                docs: [],
                numFound: 0,
                source: 'gutenberg',
                note: 'Project Gutenberg books are available at gutenberg.org but require server-side integration due to CORS restrictions'
            };
        } catch (err) {
            Logger.warn('Project Gutenberg search not available:', err);
            return {
                docs: [],
                numFound: 0,
                source: 'gutenberg',
                error: 'Project Gutenberg integration requires server-side implementation'
            };
        }
    },

    // Get Project Gutenberg book details (placeholder)
    getGutenbergBookDetails: async (bookId) => {
        return {
            id: bookId,
            source: 'gutenberg',
            note: 'Project Gutenberg books are freely available at gutenberg.org',
            url: `https://www.gutenberg.org/ebooks/${bookId}`
        };
    },

    // Search for books with multiple sources
    searchBooksMultiSource: async (query, page = 1, limit = 10, includeGutenberg = false) => {
        const results = {
            openlibrary: null,
            gutenberg: null,
            combined: []
        };

        try {
            // Search Open Library
            results.openlibrary = await this.searchBooks(query, page, limit);
            results.combined = [...(results.openlibrary.docs || [])];

            // Add Project Gutenberg results if requested
            if (includeGutenberg) {
                results.gutenberg = await this.searchGutenbergBooks(query, page, limit);
                // Note: Gutenberg results would be added here if server-side integration was available
            }

            return results;
        } catch (err) {
            Logger.error('Multi-source search error:', err);
            throw err;
        }
    }
};

export default OpenLibraryService;
